import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useAuth } from '../hooks/useAuth'
import { api } from '../lib/api'
import { 
  Users, 
  Filter,
  Edit,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  CameraOff,
  FileText,
  Calendar,
  Clock
} from 'lucide-react'
import toast from 'react-hot-toast'
import WebcamCapture from '../components/WebcamCapture'
import StudentExpose from '../components/StudentExpose'
import PermissionInfo from '../components/PermissionInfo'
import StudentGuardianContactsManager from '../components/StudentGuardianContactsManager'
import StudentEmailsManager from '../components/StudentEmailsManager'
import StudentFilesManager from '../components/StudentFilesManager'
import AttendanceCalendar from '../components/AttendanceCalendar'
import LearningPlanTasksModal from '../components/LearningPlanTasksModal'
import { loadStudentPhotos, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils'
import {
  Badge,
  Button,
  Modal,
  SortableTh,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableScrollArea,
  Tile,
  TileContent,
  TileHeader,
  TileTitle
} from '../components/ui'
import StudentsV2Toolbar from './StudentsV2Toolbar'
import StudentsV2SegmentNav, { type StudentsV2Area, type StudentsV2Subtab } from './StudentsV2SegmentNav'
import StudentKompetenzenPanel from '../components/students/StudentKompetenzenPanel'

interface Student {
  ID: number
  Name: string
  Vorname: string
  Klasse: string
  RFID: string
  sophomorixUnid: string
  email?: string
  Diff_D: string
  Diff_M: string
  Diff_E: string
  Memo?: string
  Geburtsdatum?: string
  LRS?: number
}

interface KIAssessment {
  id: number
  unid: string
  memo: string
  datum: string
  farbe: 'red' | 'yellow' | 'green' | 'gray' | 'normal'
}

interface PerformanceRecord {
  Fach: string
  Baustein: string
  Lernplan: string
  Aufgabe: string
  Lehrer: string
  Datum: string
}

interface ShortAbsenceRecord {
  id: number
  contextType: 'lernbuero_session' | 'teaching_unit'
  contextId: number
  datum: string
  startUhrzeit: string
  endUhrzeit: string | null
  dauerSekunden: number
}

interface ShortAbsenceSummary {
  totalAbsences: number
  averageDurationSeconds: number
  averagePerWeek: number
  weekCount: number
}

interface ShortAbsenceResponse {
  records: ShortAbsenceRecord[]
  summary: ShortAbsenceSummary
}



// Virtual scrolling constants
const ROW_HEIGHT = 64 // 16 * 4 (h-16)
const BUFFER_SIZE = 5 // Number of extra rows to render above/below visible area
const STUDENTS_V2_AREA_KEY = 'students-v2-active-area'
const STUDENTS_V2_SUBTAB_KEY = 'students-v2-active-subtab'

const tabToSegment: Record<'properties' | 'performance' | 'attendance' | 'shortAbsences' | 'leistungsnachweise' | 'kompetenzen', { area: StudentsV2Area; subtab: StudentsV2Subtab }> = {
  properties: { area: 'stammdaten', subtab: 'profil' },
  performance: { area: 'leistung', subtab: 'leistungsdaten' },
  attendance: { area: 'anwesenheit', subtab: 'kalender' },
  shortAbsences: { area: 'anwesenheit', subtab: 'kurzzeiten' },
  leistungsnachweise: { area: 'leistung', subtab: 'belege' },
  kompetenzen: { area: 'leistung', subtab: 'kompetenzen' }
}

const segmentToTab: Record<StudentsV2Subtab, 'properties' | 'performance' | 'attendance' | 'shortAbsences' | 'leistungsnachweise' | 'kompetenzen'> = {
  profil: 'properties',
  kontakte: 'properties',
  leistungsdaten: 'performance',
  belege: 'leistungsnachweise',
  kompetenzen: 'kompetenzen',
  kalender: 'attendance',
  kurzzeiten: 'shortAbsences'
}

export default function StudentsV2() {
  // Initialize state from sessionStorage or defaults (eigenständige Keys, parallel zur klassischen Seite /students)
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(() => {
    const saved = sessionStorage.getItem('students-v2-selected-student')
    return saved ? JSON.parse(saved) : null
  })
  
  const [activeTab, setActiveTab] = useState<'properties' | 'performance' | 'attendance' | 'shortAbsences' | 'leistungsnachweise' | 'kompetenzen'>(() => {
    const saved = sessionStorage.getItem('students-v2-active-tab')
    return (saved as 'properties' | 'performance' | 'attendance' | 'shortAbsences' | 'leistungsnachweise' | 'kompetenzen') || 'properties'
  })
  const [activeArea, setActiveArea] = useState<StudentsV2Area>(() => {
    const savedArea = sessionStorage.getItem(STUDENTS_V2_AREA_KEY) as StudentsV2Area | null
    if (savedArea === 'stammdaten' || savedArea === 'leistung' || savedArea === 'anwesenheit') {
      return savedArea
    }
    const savedTab = sessionStorage.getItem('students-v2-active-tab') as keyof typeof tabToSegment | null
    return savedTab ? tabToSegment[savedTab].area : 'stammdaten'
  })
  const [activeSubtab, setActiveSubtab] = useState<StudentsV2Subtab>(() => {
    const savedSubtab = sessionStorage.getItem(STUDENTS_V2_SUBTAB_KEY) as StudentsV2Subtab | null
    if (savedSubtab && savedSubtab in segmentToTab) return savedSubtab
    const savedTab = sessionStorage.getItem('students-v2-active-tab') as keyof typeof tabToSegment | null
    return savedTab ? tabToSegment[savedTab].subtab : 'profil'
  })
  
  const [filter, setFilter] = useState(() => {
    const saved = sessionStorage.getItem('students-v2-filter')
    return saved || ''
  })
  
  const [sortField, setSortField] = useState<'Name' | 'Vorname' | 'Klasse'>(() => {
    const saved = sessionStorage.getItem('students-v2-sort-field')
    return (saved as 'Name' | 'Vorname' | 'Klasse') || 'Name'
  })
  
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const saved = sessionStorage.getItem('students-v2-sort-direction')
    return (saved as 'asc' | 'desc') || 'asc'
  })
  
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState<Partial<Student>>({})
  const [isEditingMemo, setIsEditingMemo] = useState(false)
  const [memoText, setMemoText] = useState('')
  const [showStudentList, setShowStudentList] = useState(true)
  
  // Performance data sorting state
  const [performanceSortField, setPerformanceSortField] = useState<'Fach' | 'Baustein' | 'Lernplan' | 'Aufgabe' | 'Lehrer' | 'Datum'>('Datum')
  const [performanceSortDirection, setPerformanceSortDirection] = useState<'asc' | 'desc'>('desc')
  
  // Performance data filtering state
  const [performanceSearch, setPerformanceSearch] = useState('')
  const [performanceStartDate, setPerformanceStartDate] = useState('')
  const [performanceEndDate, setPerformanceEndDate] = useState('')
  
  // Learning plans (Belege) state
  const [learningPlansSearch, setLearningPlansSearch] = useState('')
  const [learningPlansSortField, setLearningPlansSortField] = useState<'NAME' | 'FACH' | 'BAUSTEIN' | 'START_DATUM' | 'END_DATUM'>('START_DATUM')
  const [learningPlansSortDirection, setLearningPlansSortDirection] = useState<'asc' | 'desc'>('desc')
  const [selectedLearningPlan, setSelectedLearningPlan] = useState<any | null>(null)
  const [showLearningPlanModal, setShowLearningPlanModal] = useState(false)
  
  // Add new student modal state
  const [showAddModal, setShowAddModal] = useState(false)
  
  // Student photos state with LRU cache
  const [studentPhotos, setStudentPhotos] = useState<Record<string, string | null>>({})
  const [photoCacheOrder, setPhotoCacheOrder] = useState<string[]>([])
  const MAX_CACHE_SIZE = 100 // Maximum number of photos to keep in memory
  const [newStudentForm, setNewStudentForm] = useState<Partial<Student>>({
    Name: '',
    Vorname: '',
    Klasse: '',
    email: '',
    RFID: '',
    Diff_D: 'GK',
    Diff_M: 'GK',
    Diff_E: 'GK'
  })
  
  // Expose modal state
  const [showExposeModal, setShowExposeModal] = useState(false)
  
  // Virtual scrolling state
  const [scrollTop, setScrollTop] = useState(0)
  const [containerHeight, setContainerHeight] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // User assignments state for class teachers
  const [userAssignments, setUserAssignments] = useState<{
    classes: Array<{
      assignment_type: string
      assignment_value: string
      is_active: boolean
    }>
  } | null>(null)

  // Load user assignments for class teachers
  useEffect(() => {
    if (user?.role === 'klassenlehrer') {
      api.get('/assignments/my-assignments')
        .then(response => {
          if (response.data.success) {
            setUserAssignments(response.data.data)
          }
        })
        .catch(error => {
          console.error('Error loading user assignments:', error)
        })
    } else {
      setUserAssignments(null)
    }
  }, [user?.role])

  // Helper function to normalize class names (remove leading zeros)
  const normalizeClassName = (className: string | null | undefined): string => {
    if (!className) return ''
    return className.replace(/^0+/, '') || '0'
  }

  // Check if user can edit a specific student
  const canEditStudent = (student: Student | null): boolean => {
    if (!student || !user) return false

    const userRole = user.role?.toLowerCase()

    // Administratoren, Schulleitung und Abteilungsleiter haben immer Zugriff
    if (['admin', 'administrator', 'schulleitung', 'abteilungsleiter'].includes(userRole)) {
      return true
    }

    // Klassenlehrer haben nur Zugriff auf ihre zugewiesenen Klassen
    if (userRole === 'klassenlehrer') {
      if (!userAssignments?.classes || userAssignments.classes.length === 0) {
        return false
      }

      const normalizedStudentClass = normalizeClassName(student.Klasse)
      const hasAccess = userAssignments.classes.some(assignment => {
        if (assignment.assignment_type !== 'class' || !assignment.is_active) {
          return false
        }
        const normalizedAssignmentClass = normalizeClassName(assignment.assignment_value)
        return normalizedAssignmentClass === normalizedStudentClass
      })

      return hasAccess
    }

    // Alle anderen Rollen haben nur Read-Only Zugriff
    return false
  }

  // Check if current selected student can be edited
  const canEditCurrentStudent = canEditStudent(selectedStudent)

  // Fetch students with sorting and filtering (no pagination)
  // Photos are NOT loaded in list view for performance (loaded on-demand when student is selected)
  const { data: studentsResponse, isLoading } = useQuery(
    ['students', sortField, sortDirection, filter],
    () => api.get('/students', {
      params: {
        sortField,
        sortDirection,
        search: filter || undefined,
        includePhotos: 'false' // Photos loaded separately on-demand for better performance
      }
    }).then(res => res.data)
  )

  const students = studentsResponse?.data || []

  // Virtual scrolling calculations (using filtered students)
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_SIZE)
  const endIndex = Math.min(
    students.length,
    Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + BUFFER_SIZE
  )
  const visibleStudents = students.slice(startIndex, endIndex)
  const totalHeight = Math.max(students.length * ROW_HEIGHT, 100) // Minimum height to prevent empty container
  const offsetY = startIndex * ROW_HEIGHT

  // LRU Cache helper functions
  const updatePhotoCache = useCallback((studentId: string, photo: string | null) => {
    setStudentPhotos(prev => {
      const newPhotos = { ...prev, [studentId]: photo };
      
      // Update cache order
      setPhotoCacheOrder(prevOrder => {
        const newOrder = prevOrder.filter(id => id !== studentId);
        newOrder.push(studentId);
        
        // Remove oldest entries if cache is too large
        if (newOrder.length > MAX_CACHE_SIZE) {
          const toRemove = newOrder.splice(0, newOrder.length - MAX_CACHE_SIZE);
          toRemove.forEach(id => delete newPhotos[id]);
        }
        
        return newOrder;
      });
      
      return newPhotos;
    });
  }, [MAX_CACHE_SIZE]);

  // Load photos only for visible students (Lazy Loading) with debouncing
  useEffect(() => {
    if (visibleStudents.length > 0) {
      const visibleStudentIds = visibleStudents
        .map((s: Student) => s.sophomorixUnid)
        .filter((id: string) => {
          if (!id || id === 'undefined' || id === 'null') {
            return false;
          }

          // Skip students whose photo was already requested (null means "no photo")
          return !Object.prototype.hasOwnProperty.call(studentPhotos, id);
        });
      
      if (visibleStudentIds.length > 0) {
        // Debounce photo loading to prevent too many requests
        const timeoutId = setTimeout(() => {
          // Load photos in smaller batches for visible students only
          const batchSize = 20; // Smaller batches for better performance
          const batches = [];
          for (let i = 0; i < visibleStudentIds.length; i += batchSize) {
            batches.push(visibleStudentIds.slice(i, i + batchSize));
          }
          
          // Load each batch with delay to prevent overwhelming the server
          batches.forEach((batch, index) => {
            setTimeout(() => {
              loadStudentPhotos(batch).then(photos => {
                // Update cache for each photo directly without using updatePhotoCache function
                setStudentPhotos(prev => {
                  const newPhotos = { ...prev };
                  Object.entries(photos).forEach(([studentId, photo]) => {
                    newPhotos[studentId] = photo;
                  });
                  
                  // Update cache order
                  setPhotoCacheOrder(prevOrder => {
                    const newOrder = [...prevOrder];
                    Object.keys(photos).forEach(studentId => {
                      // Remove from current position and add to end
                      const index = newOrder.indexOf(studentId);
                      if (index > -1) {
                        newOrder.splice(index, 1);
                      }
                      newOrder.push(studentId);
                    });
                    
                    // Remove oldest entries if cache is too large
                    if (newOrder.length > MAX_CACHE_SIZE) {
                      const toRemove = newOrder.splice(0, newOrder.length - MAX_CACHE_SIZE);
                      toRemove.forEach(id => delete newPhotos[id]);
                    }
                    
                    return newOrder;
                  });
                  
                  return newPhotos;
                });
              }).catch(error => {
                console.warn('Fehler beim Laden der sichtbaren Schülerfotos:', error);
              });
            }, index * 200); // Increased delay to 200ms
          });
        }, 300); // 300ms debounce delay
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [visibleStudents, studentPhotos]); // Removed updatePhotoCache from dependencies

  // Load photo for selected student if not already cached
  useEffect(() => {
    const studentId = selectedStudent?.sophomorixUnid;
    if (studentId && !Object.prototype.hasOwnProperty.call(studentPhotos, studentId)) {
      loadStudentPhotos([studentId]).then(photos => {
        const photo = photos[studentId];
        if (photo !== undefined) {
          // Update cache directly without using updatePhotoCache function
          setStudentPhotos(prev => {
            const newPhotos = { ...prev, [studentId]: photo };

            // Update cache order
            setPhotoCacheOrder(prevOrder => {
              const newOrder = prevOrder.filter(id => id !== studentId);
              newOrder.push(studentId);
              
              // Remove oldest entries if cache is too large
              if (newOrder.length > MAX_CACHE_SIZE) {
                const toRemove = newOrder.splice(0, newOrder.length - MAX_CACHE_SIZE);
                toRemove.forEach(id => delete newPhotos[id]);
              }
              
              return newOrder;
            });
            
            return newPhotos;
          });
        }
      }).catch(error => {
        console.warn('Fehler beim Laden des ausgewählten Schülerfotos:', error);
      });
    }
  }, [selectedStudent?.sophomorixUnid, studentPhotos]); // Removed updatePhotoCache from dependencies

  // Get photo for selected student
  const selectedStudentPhoto = selectedStudent ? studentPhotos[selectedStudent.sophomorixUnid] : null;

  // Restore selected student after data loads if it was saved in sessionStorage
  useEffect(() => {
    if (students.length > 0 && selectedStudent) {
      // Check if the selected student still exists in the current data
      const studentExists = students.find((s: Student) => s.sophomorixUnid === selectedStudent.sophomorixUnid)
      if (!studentExists) {
        // Selected student no longer exists, clear it
        setSelectedStudent(null)
        sessionStorage.removeItem('students-v2-selected-student')
      }
    }
  }, [students, selectedStudent])



  // Update container height on mount and resize
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        // Find the properties tab content to match its height
        const propertiesTab = document.querySelector('[data-tab="properties"]')
        if (propertiesTab) {
          const propertiesHeight = propertiesTab.scrollHeight
          // Set the table height to match the properties tab height
          setContainerHeight(propertiesHeight)
        } else {
          // Fallback height if properties tab is not found
          setContainerHeight(600)
        }
      }
    }
    
    // Initial update with a small delay to ensure DOM is ready
    const timer = setTimeout(updateHeight, 100)
    
    // Also update when tab changes
    const tabChangeTimer = setTimeout(updateHeight, 200)
    
    return () => {
      clearTimeout(timer)
      clearTimeout(tabChangeTimer)
    }
  }, [activeTab])

     // Handle scroll
   const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
     setScrollTop(e.currentTarget.scrollTop)
   }, [])

   // Update editForm when selectedStudent changes (including after data refresh)
   useEffect(() => {
     if (selectedStudent) {
       setEditForm(selectedStudent)
     }
   }, [selectedStudent])

  // Fetch KI assessment for selected student
  const { data: kiAssessment } = useQuery<KIAssessment>(
    ['ki-assessment', selectedStudent?.sophomorixUnid],
    () => api.get(`/ki/assessment/${selectedStudent?.sophomorixUnid}`).then(res => res.data.data),
    { 
      enabled: !!selectedStudent?.sophomorixUnid && 
               selectedStudent.sophomorixUnid !== 'undefined' && 
               selectedStudent.sophomorixUnid !== 'null' &&
               selectedStudent.sophomorixUnid !== '',
      retry: false,
      onError: (error: any) => {
        // Silently handle 404 errors for missing assessments
        if (error?.response?.status !== 404) {
          console.error('KI assessment error:', error);
        }
      }
    }
  )

  // Fetch performance data for selected student
  const { data: performanceData } = useQuery<PerformanceRecord[]>(
    ['student-performance', selectedStudent?.sophomorixUnid, performanceSortField, performanceSortDirection, performanceSearch, performanceStartDate, performanceEndDate],
    () => api.get(`/students/${selectedStudent?.sophomorixUnid}/performance`, {
      params: {
        sortField: performanceSortField,
        sortDirection: performanceSortDirection,
        search: performanceSearch || undefined,
        startDate: performanceStartDate || undefined,
        endDate: performanceEndDate || undefined
      }
    }).then(res => res.data.data),
    { 
      enabled: !!selectedStudent?.sophomorixUnid && 
               selectedStudent.sophomorixUnid !== 'undefined' && 
               selectedStudent.sophomorixUnid !== 'null' &&
               selectedStudent.sophomorixUnid !== '',
      retry: false,
      onError: (error: any) => {
        // Silently handle 404 errors for missing performance data
        if (error?.response?.status !== 404) {
          console.error('Performance data error:', error);
        }
      }
    }
  )

  // Fetch learning plans for selected student
  const { data: learningPlansData, isLoading: isLoadingLearningPlans } = useQuery<any[]>(
    ['student-learning-plans', selectedStudent?.sophomorixUnid],
    () => api.get(`/students/${selectedStudent?.sophomorixUnid}/learning-plans`).then(res => res.data.data),
    {
      enabled: !!selectedStudent?.sophomorixUnid && 
               selectedStudent.sophomorixUnid !== 'undefined' && 
               selectedStudent.sophomorixUnid !== 'null' &&
               selectedStudent.sophomorixUnid !== '',
      retry: false,
      onError: (error: any) => {
        if (error?.response?.status !== 404) {
          console.error('Learning plans error:', error);
        }
      }
    }
  )

  const { data: shortAbsencesData, isLoading: isLoadingShortAbsences } = useQuery<ShortAbsenceResponse>(
    ['student-short-absences', selectedStudent?.sophomorixUnid],
    () => api.get(`/students/${selectedStudent?.sophomorixUnid}/short-absences`).then(res => res.data.data),
    {
      enabled: !!selectedStudent?.sophomorixUnid &&
               selectedStudent.sophomorixUnid !== 'undefined' &&
               selectedStudent.sophomorixUnid !== 'null' &&
               selectedStudent.sophomorixUnid !== '',
      retry: false,
      onError: (error: any) => {
        if (error?.response?.status !== 404) {
          console.error('Short absences error:', error)
        }
      }
    }
  )

  const formatShortAbsenceDuration = (seconds: number) => {
    const safeSeconds = Math.max(0, Number(seconds || 0))
    const minutes = Math.floor(safeSeconds / 60)
    const remainingSeconds = safeSeconds % 60
    if (minutes > 0) return `${minutes} Min`
    return `${remainingSeconds} Sek`
  }

  const shortAbsenceRecords = shortAbsencesData?.records || []
  const shortAbsenceSummary = shortAbsencesData?.summary



  // Update student mutation
  const updateStudentMutation = useMutation(
    (data: Partial<Student>) => api.put(`/students/unid/${data.sophomorixUnid}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('students')
        setIsEditing(false)
        toast.success('Schüler erfolgreich aktualisiert')
      },
      onError: (error: any) => {
        console.error('Error updating student:', error)
        
        // Handle permission errors specifically
        if (error?.response?.status === 403) {
          const message = error?.response?.data?.message || 'Keine Berechtigung zum Bearbeiten dieses Schülers'
          toast.error(
            <div>
              <div className="font-semibold">Zugriff verweigert</div>
              <div className="text-sm mt-1">{message}</div>
            </div>,
            { duration: 6000 }
          )
        } else if (error?.response?.status === 401) {
          toast.error('Bitte melden Sie sich erneut an')
        } else {
          const message = error?.response?.data?.message || 'Fehler beim Aktualisieren des Schülers'
          toast.error(message)
        }
      }
    }
  )

  // LDAP Sync mutation
  const ldapSyncMutation = useMutation(
    () => api.post('/students/ldap-sync'),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('students')
        const result = data.data.data
        toast.success(
          <div>
            <div className="font-semibold">LDAP-Synchronisation erfolgreich</div>
            <div className="text-sm mt-1">
              {result.processed} Schüler verarbeitet • {result.newStudents} neu • {result.updatedStudents} aktualisiert
            </div>
          </div>,
          { duration: 5000 }
        )
      },
      onError: (error: any) => {
        console.error('LDAP sync error:', error)
        if (error?.response?.status === 403) {
          toast.error('Keine Berechtigung für LDAP-Synchronisation. Nur Administratoren können diese Funktion nutzen.')
        } else if (error?.response?.status === 401) {
          toast.error('Bitte melden Sie sich erneut an')
        } else {
          const message = error?.response?.data?.message || 'Fehler bei der LDAP-Synchronisation'
          toast.error(message)
        }
      }
    }
  )

  // Adopt card ID mutation
  const adoptCardIdMutation = useMutation(
    (studentId: string) => api.post(`/students/${studentId}/adopt-card-id`),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('students')
        toast.success(data.data.message || 'Karten ID erfolgreich übernommen')
        // Update the edit form with the new RFID
        if (data.data?.data?.cardId) {
          setEditForm(prev => ({ ...prev, RFID: data.data.data.cardId }))
        }
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 'Fehler beim Übernehmen der Karten ID'
        toast.error(message)
      }
    }
  )

  // Update student memo mutation
  const updateStudentMemoMutation = useMutation(
    (data: { sophomorixUnid: string; memo: string }) => api.put(`/students/memo/${data.sophomorixUnid}`, { memo: data.memo }),
    {
      onSuccess: (response, variables) => {
        // Update the selectedStudent state immediately with the new memo
        if (selectedStudent) {
          setSelectedStudent({
            ...selectedStudent,
            Memo: variables.memo
          })
        }
        queryClient.invalidateQueries('students')
        setIsEditingMemo(false)
        toast.success('Notizen erfolgreich aktualisiert')
      },
      onError: (error: any) => {
        console.error('Error updating student memo:', error)
        
        // Handle permission errors specifically
        if (error?.response?.status === 403) {
          const message = error?.response?.data?.message || 'Keine Berechtigung zum Bearbeiten der Notizen für diesen Schüler'
          toast.error(
            <div>
              <div className="font-semibold">Zugriff verweigert</div>
              <div className="text-sm mt-1">{message}</div>
            </div>,
            { duration: 6000 }
          )
        } else if (error?.response?.status === 401) {
          toast.error('Bitte melden Sie sich erneut an')
        } else {
          const message = error?.response?.data?.message || 'Fehler beim Aktualisieren der Notizen'
          toast.error(message)
        }
      }
    }
  )

  // Transfer student to learning office
  const transferToLearningOfficeMutation = useMutation(
    (studentId: number) => api.post(`/students/${studentId}/transfer-learning-office`),
    {
      onSuccess: () => {
        toast.success('Schüler ins Lernbüro übernommen')
      },
      onError: () => {
        toast.error('Fehler beim Übernehmen ins Lernbüro')
      }
    }
  )

  // Sync user data
  const syncUserDataMutation = useMutation(
    () => api.post('/students/sync-user-data'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('students')
        toast.success('Benutzerdaten erfolgreich abgeglichen')
      },
      onError: () => {
        toast.error('Fehler beim Abgleichen der Benutzerdaten')
      }
    }
  )

  // Create new student mutation
  const createStudentMutation = useMutation(
    (data: Partial<Student>) => api.post('/students', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('students')
        toast.success('Schüler erfolgreich erstellt')
        // Reset form and close modal
        setNewStudentForm({
          Name: '',
          Vorname: '',
          Klasse: '',
          email: '',
          RFID: '',
          Diff_D: 'GK',
          Diff_M: 'GK',
          Diff_E: 'GK'
        })
        setShowAddModal(false)
      },
      onError: () => {
        toast.error('Fehler beim Erstellen des Schülers')
      }
    }
  )

  // Handle sorting
  const handleSort = (field: 'Name' | 'Vorname' | 'Klasse') => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
      setSortDirection(newDirection)
      sessionStorage.setItem('students-v2-sort-direction', newDirection)
    } else {
      setSortField(field)
      setSortDirection('asc')
      sessionStorage.setItem('students-v2-sort-field', field)
      sessionStorage.setItem('students-v2-sort-direction', 'asc')
    }
  }

  // Handle filter change
  const handleFilterChange = (value: string) => {
    setFilter(value)
    sessionStorage.setItem('students-v2-filter', value)
  }

  // Handle performance data sorting
  const handlePerformanceSort = (field: 'Fach' | 'Baustein' | 'Lernplan' | 'Aufgabe' | 'Lehrer' | 'Datum') => {
    if (performanceSortField === field) {
      const newDirection = performanceSortDirection === 'asc' ? 'desc' : 'asc'
      setPerformanceSortDirection(newDirection)
    } else {
      setPerformanceSortField(field)
      setPerformanceSortDirection('asc')
    }
  }

  // Handle performance data filtering
  const handlePerformanceSearchChange = (value: string) => {
    setPerformanceSearch(value)
  }

  const handlePerformanceStartDateChange = (value: string) => {
    setPerformanceStartDate(value)
  }

  const handlePerformanceEndDateChange = (value: string) => {
    setPerformanceEndDate(value)
  }

  const clearPerformanceFilters = () => {
    setPerformanceSearch('')
    setPerformanceStartDate('')
    setPerformanceEndDate('')
  }

  const setTabAndPersist = (nextTab: 'properties' | 'performance' | 'attendance' | 'shortAbsences' | 'leistungsnachweise' | 'kompetenzen') => {
    setActiveTab(nextTab)
    sessionStorage.setItem('students-v2-active-tab', nextTab)
  }

  const handleAreaChange = (area: StudentsV2Area) => {
    const nextSubtab =
      area === 'stammdaten' ? 'profil' :
      area === 'leistung' ? 'leistungsdaten' :
      'kalender'
    setActiveArea(area)
    setActiveSubtab(nextSubtab)
    sessionStorage.setItem(STUDENTS_V2_AREA_KEY, area)
    sessionStorage.setItem(STUDENTS_V2_SUBTAB_KEY, nextSubtab)
    setTabAndPersist(segmentToTab[nextSubtab])
  }

  const handleSubtabChange = (subtab: StudentsV2Subtab) => {
    const nextArea =
      subtab === 'profil' || subtab === 'kontakte' ? 'stammdaten' :
      subtab === 'leistungsdaten' || subtab === 'belege' || subtab === 'kompetenzen' ? 'leistung' :
      'anwesenheit'
    setActiveArea(nextArea)
    setActiveSubtab(subtab)
    sessionStorage.setItem(STUDENTS_V2_AREA_KEY, nextArea)
    sessionStorage.setItem(STUDENTS_V2_SUBTAB_KEY, subtab)
    setTabAndPersist(segmentToTab[subtab])
  }

  // Handle student selection
  const handleStudentSelect = useCallback((student: Student) => {
    // Safeguard: ensure we're selecting a single student object
    if (!student || typeof student !== 'object' || Array.isArray(student)) {
      console.warn('Invalid student selection:', student)
      return
    }
    
    // Safeguard: ensure sophomorixUnid exists
    if (!student.sophomorixUnid) {
      console.warn('Student has no sophomorixUnid:', student)
      return
    }
    
    // Additional safeguard: verify student exists in current data
    const studentExists = students.find((s: Student) => s.sophomorixUnid === student.sophomorixUnid)
    
    if (!studentExists) {
      console.warn('Selected student not found in current data:', {
        sophomorixUnid: student.sophomorixUnid,
        Name: student.Name,
        Vorname: student.Vorname
      })
      return
    }
    
    // Use the student from the full list to avoid virtual scrolling issues
    setSelectedStudent(studentExists)
    setEditForm(studentExists)
    setIsEditing(false)
    setIsEditingMemo(false)
    setMemoText(studentExists.Memo || '')
    // Save selected student to sessionStorage
    sessionStorage.setItem('students-v2-selected-student', JSON.stringify(studentExists))
    // Trigger screen data update for Tom
    window.dispatchEvent(new CustomEvent('screenDataUpdate'))
  }, [students])

  // Handle save
  const handleSave = () => {
    if (selectedStudent && editForm) {
      // Only include fields that have actually changed
      const changedFields: Partial<Student> = {};
      
      // Check each field against the original student data
      Object.keys(editForm).forEach(key => {
        const fieldKey = key as keyof Student;
        if (editForm[fieldKey] !== selectedStudent[fieldKey]) {
          changedFields[fieldKey] = editForm[fieldKey] as any;
        }
      });
      
      // Always include the sophomorixUnid for the update
      changedFields.sophomorixUnid = selectedStudent.sophomorixUnid;
      
      // Only proceed if there are actual changes
      if (Object.keys(changedFields).length > 1) { // More than just sophomorixUnid
        updateStudentMutation.mutate(changedFields);
      } else {
        toast.success('Keine Änderungen zum Speichern');
      }
    }
  }

  // Handle save memo
  const handleSaveMemo = () => {
    if (selectedStudent && memoText !== selectedStudent.Memo) {
      updateStudentMemoMutation.mutate({
        sophomorixUnid: selectedStudent.sophomorixUnid,
        memo: memoText
      });
    } else {
      setIsEditingMemo(false);
    }
  }

  // Handle cancel memo edit
  const handleCancelMemoEdit = () => {
    setMemoText(selectedStudent?.Memo || '');
    setIsEditingMemo(false);
  }

  // Handle add new student
  const handleAddStudent = () => {
    setShowAddModal(true)
  }

  const handleLdapSync = () => {
    if (confirm('Möchten Sie die Schülerdaten mit LDAP synchronisieren? Dies kann einige Zeit dauern.')) {
      ldapSyncMutation.mutate()
    }
  }

  // Handle new student form change
  const handleNewStudentFormChange = (field: keyof Student, value: string) => {
    setNewStudentForm(prev => ({ ...prev, [field]: value }))
  }

  // Handle create new student
  const handleCreateStudent = () => {
    if (!newStudentForm.Name || !newStudentForm.Vorname || !newStudentForm.Klasse) {
      toast.error('Name, Vorname und Klasse sind erforderlich')
      return
    }
    createStudentMutation.mutate(newStudentForm)
  }

  // Handle adopt card ID
  const handleAdoptCardId = () => {
    if (!selectedStudent?.sophomorixUnid) {
      toast.error('Kein Schüler ausgewählt')
      return
    }
    
    adoptCardIdMutation.mutate(selectedStudent.sophomorixUnid)
  }



  const getKIAssessmentLevel = (assessment: KIAssessment) => {
    const fromApi = String(assessment?.farbe || '').toLowerCase()
    if (['red', 'yellow', 'green', 'gray'].includes(fromApi)) return fromApi

    const memo = String(assessment?.memo || '').toLowerCase()
    if (memo.includes('roter alarm') || memo.includes('dringender handlungsbedarf')) return 'red'
    if (memo.includes('gelber alarm') || memo.includes('beobachtung empfohlen')) return 'yellow'
    if (memo.includes('grüner alarm') || memo.includes('gruener alarm') || memo.includes('kein akuter handlungsbedarf') || memo.includes('gut')) return 'green'
    if (memo.includes('grauer alarm') || memo.includes('datenlage zu dünn') || memo.includes('datenlage zu duenn')) return 'gray'
    return 'normal'
  }

  // Get KI assessment color
  const getKIAssessmentColor = (assessment: KIAssessment) => {
    const level = getKIAssessmentLevel(assessment)
    if (level === 'red') {
      return 'bg-red-100 border-red-300 text-red-800 dark:bg-red-950/40 dark:border-red-800 dark:text-red-200'
    }
    if (level === 'yellow') {
      return 'bg-amber-100 border-amber-300 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200'
    }
    if (level === 'green') {
      return 'bg-green-100 border-green-300 text-green-800 dark:bg-green-950/40 dark:border-green-800 dark:text-green-200'
    }
    if (level === 'gray') {
      return 'bg-slate-100 border-slate-300 text-slate-800 dark:bg-slate-900/50 dark:border-slate-700 dark:text-slate-200'
    }
    return 'bg-gray-100 border-gray-300 text-gray-800 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-200'
  }

  const getKIAssessmentBadgeVariant = (assessment: KIAssessment) => {
    const level = getKIAssessmentLevel(assessment)
    if (level === 'red') return 'danger' as const
    if (level === 'yellow') return 'warning' as const
    if (level === 'green') return 'success' as const
    return 'neutral' as const
  }

  const getKIAssessmentLabel = (assessment: KIAssessment) => {
    const level = getKIAssessmentLevel(assessment)
    if (level === 'red') return 'Roter Alarm'
    if (level === 'yellow') return 'Gelber Alarm'
    if (level === 'green') return 'Grüner Alarm'
    if (level === 'gray') return 'Grauer Alarm'
    return 'Hinweis'
  }

  
  
     return (
     <div className="flex min-h-0 flex-col gap-4">
      <Tile className="px-4 py-4 sm:px-6">
        <StudentsV2Toolbar
          filter={filter}
          studentCount={students.length}
          canAddStudent={user?.role === 'administrator' || user?.role === 'admin' || user?.role === 'schulleitung' || user?.role === 'abteilungsleiter'}
          canSyncLdap={user?.role === 'administrator' || user?.role === 'admin'}
          ldapLoading={ldapSyncMutation.isLoading}
          showStudentList={showStudentList}
          onFilterChange={handleFilterChange}
          onAddStudent={handleAddStudent}
          onLdapSync={handleLdapSync}
          onToggleStudentList={() => setShowStudentList((prev) => !prev)}
        />
        <PermissionInfo context="students" />
      </Tile>

      {/* Main Content */}
      <div className="flex-1">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(300px,380px)_1fr]">
          {/* Student List */}
          <div className={`${showStudentList ? 'flex' : 'hidden'} flex-col`}>
            <Tile className="flex h-full min-h-[36rem] flex-col">
              <TileHeader className="border-b border-gray-200 dark:border-gray-700">
                <TileTitle as="h3" className="flex items-center gap-2 text-sm">
                  <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  Schülerliste
                </TileTitle>
              </TileHeader>
              <TileContent className="flex min-h-0 flex-1 flex-col p-0">

                             {/* Virtual Scrolling Container */}
               <div 
                 ref={containerRef}
                 className="relative overflow-y-auto"
                 onScroll={handleScroll}
                 style={{ 
                   height: containerHeight > 0 ? containerHeight : '400px'
                 }}
               >
                                 {/* Table Header - Fixed */}
                 <div className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/90">
                   <div className="grid grid-cols-4">
                     <div className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                       Foto
                     </div>
                     <div 
                       className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/60"
                       onClick={() => handleSort('Name')}
                     >
                       <div className="flex items-center space-x-1">
                         <span>Name</span>
                         {sortField === 'Name' && (
                           <span className="text-gray-400">
                             {sortDirection === 'asc' ? '↑' : '↓'}
                           </span>
                         )}
                       </div>
                     </div>
                     <div 
                       className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/60"
                       onClick={() => handleSort('Vorname')}
                     >
                       <div className="flex items-center space-x-1">
                         <span>Vorname</span>
                         {sortField === 'Vorname' && (
                           <span className="text-gray-400">
                             {sortDirection === 'asc' ? '↑' : '↓'}
                           </span>
                         )}
                       </div>
                     </div>
                     <div 
                       className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/60"
                       onClick={() => handleSort('Klasse')}
                     >
                       <div className="flex items-center space-x-1">
                         <span>Klasse</span>
                         {sortField === 'Klasse' && (
                           <span className="text-gray-400">
                             {sortDirection === 'asc' ? '↑' : '↓'}
                           </span>
                         )}
                       </div>
                     </div>
                   </div>
                 </div>

                {/* Virtual Scrolling Content */}
                <div style={{ height: totalHeight, position: 'relative' }}>
                  {students.length === 0 ? (
                    <div className="flex h-32 items-center justify-center text-gray-500 dark:text-gray-400">
                      <div className="text-center">
                        <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>Keine Schüler gefunden</p>
                        {filter && (
                          <p className="text-sm mt-1">Filter: "{filter}"</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ transform: `translateY(${offsetY}px)` }}>
                      {visibleStudents.map((student: Student, index: number) => {
                      const actualIndex = startIndex + index
                      return (
                         <div
                           key={student.sophomorixUnid}
                           onClick={() => handleStudentSelect(student)}
                           className={`grid h-16 grid-cols-4 cursor-pointer items-center border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/60 ${
                             selectedStudent?.sophomorixUnid === student.sophomorixUnid
                               ? 'border-l-4 border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                               : 'bg-white dark:bg-gray-900'
                           }`}
                         >
                           <div className="flex items-center justify-center">
                             {hasPhoto(studentPhotos[student.sophomorixUnid]) ? (
                               <img
                                 src={createPhotoDataUrl(studentPhotos[student.sophomorixUnid]!)}
                                 alt={`${student.Vorname} ${student.Name}`}
                                className="h-10 w-10 flex-shrink-0 rounded-full border-2 border-gray-200 object-cover dark:border-gray-700"
                                 onError={(e) => {
                                   // Fallback zu Platzhalter bei Fehler
                                   const target = e.target as HTMLImageElement;
                                   target.style.display = 'none';
                                   const parent = target.parentElement;
                                   if (parent) {
                                     parent.innerHTML = '<div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0"><svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                                   }
                                 }}
                               />
                             ) : (
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
                                 <Users className="h-5 w-5 text-gray-400" />
                               </div>
                             )}
                           </div>
                           <div className="min-w-0 px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                             <div className="truncate">{student.Name || ''}</div>
                           </div>
                           <div className="min-w-0 px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                             <div className="truncate">{student.Vorname || ''}</div>
                           </div>
                           <div className="min-w-0 px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                             <div className="truncate">{student.Klasse || ''}</div>
                           </div>
                         </div>
                      )
                    })}
                    </div>
                  )}
                </div>

                {/* Loading indicator */}
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/75 dark:bg-gray-900/75">
                    <div className="flex items-center space-x-2">
                      <RefreshCw className="h-5 w-5 animate-spin text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">Lade Schüler...</span>
                    </div>
                  </div>
                )}
              </div>
              </TileContent>
            </Tile>
          </div>

                     {/* Student Details */}
           <div className={`${showStudentList ? '' : 'xl:col-span-1'} flex flex-col`}>
             <div className="space-y-6">
              {selectedStudent ? (
                <>
                  <Tile className="overflow-hidden">
                    <StudentsV2SegmentNav
                      area={activeArea}
                      subtab={activeSubtab}
                      onAreaChange={handleAreaChange}
                      onSubtabChange={handleSubtabChange}
                    />
                    <div className="p-4">
                         {activeTab === 'properties' && (
                           <div className="space-y-4" data-tab="properties">
                            {activeSubtab !== 'kontakte' && (
                            <>
                            {/* Schülerdaten bearbeiten - Compact Section */}
                            <div className="rounded-lg border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-base font-semibold text-gray-800 dark:text-gray-100 flex items-center">
                                  <Edit className="h-4 w-4 mr-2 text-blue-600" />
                                  Schülerdaten bearbeiten
                                </h4>
                                {!canEditCurrentStudent && (
                                  <span className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                    Nur Lesezugriff
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Name</label>
                                  <input
                                    type="text"
                                    value={editForm.Name || ''}
                                    onChange={(e) => setEditForm({ ...editForm, Name: e.target.value })}
                                    disabled={!canEditCurrentStudent}
                                    className={`input mt-1 w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm py-1 ${!canEditCurrentStudent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="Name eingeben"
                                    readOnly={!canEditCurrentStudent}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Vorname</label>
                                  <input
                                    type="text"
                                    value={editForm.Vorname || ''}
                                    onChange={(e) => setEditForm({ ...editForm, Vorname: e.target.value })}
                                    disabled={!canEditCurrentStudent}
                                    className={`input mt-1 w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm py-1 ${!canEditCurrentStudent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="Vorname eingeben"
                                    readOnly={!canEditCurrentStudent}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Klasse</label>
                                  <input
                                    type="text"
                                    value={editForm.Klasse || ''}
                                    onChange={(e) => setEditForm({ ...editForm, Klasse: e.target.value })}
                                    disabled={!canEditCurrentStudent}
                                    className={`input mt-1 w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm py-1 ${!canEditCurrentStudent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="Klasse eingeben"
                                    readOnly={!canEditCurrentStudent}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">RFID/Chipkarten ID</label>
                                  <input
                                    type="text"
                                    value={editForm.RFID || ''}
                                    onChange={(e) => setEditForm({ ...editForm, RFID: e.target.value })}
                                    disabled={!canEditCurrentStudent}
                                    className={`input mt-1 w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm py-1 ${!canEditCurrentStudent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="RFID eingeben"
                                    readOnly={!canEditCurrentStudent}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Geburtsdatum</label>
                                  <div className="relative">
                                    <input
                                      type="text"
                                      value={editForm.Geburtsdatum 
                                        ? (() => {
                                            try {
                                              // Handle YYYY-MM-DD format
                                              if (editForm.Geburtsdatum.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                                const [year, month, day] = editForm.Geburtsdatum.split('-');
                                                return `${day}.${month}.${year}`;
                                              }
                                              // Handle DD.MM.YYYY format (user is typing)
                                              if (editForm.Geburtsdatum.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
                                                return editForm.Geburtsdatum;
                                              }
                                              // Try to parse as date
                                              const date = new Date(editForm.Geburtsdatum);
                                              if (!isNaN(date.getTime())) {
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                return `${day}.${month}.${year}`;
                                              }
                                            } catch {}
                                            return editForm.Geburtsdatum;
                                          })()
                                        : ''}
                                      onChange={(e) => {
                                        const value = e.target.value.trim();
                                        if (value === '') {
                                          setEditForm({ ...editForm, Geburtsdatum: undefined });
                                          return;
                                        }
                                        // Parse DD.MM.YYYY format
                                        const match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
                                        if (match) {
                                          const [, day, month, year] = match;
                                          const dayNum = parseInt(day, 10);
                                          const monthNum = parseInt(month, 10);
                                          const yearNum = parseInt(year, 10);
                                          
                                          // Validate date
                                          if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12 && yearNum >= 1900 && yearNum <= 2100) {
                                            const date = new Date(yearNum, monthNum - 1, dayNum);
                                            if (date.getDate() === dayNum && date.getMonth() === monthNum - 1 && date.getFullYear() === yearNum) {
                                              // Valid date, convert to YYYY-MM-DD for storage
                                              const isoDate = `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                                              setEditForm({ ...editForm, Geburtsdatum: isoDate });
                                              return;
                                            }
                                          }
                                        }
                                        // If not matching format, just store as-is (user is still typing)
                                        setEditForm({ ...editForm, Geburtsdatum: value });
                                      }}
                                      onBlur={(e) => {
                                        const value = e.target.value.trim();
                                        if (value === '') {
                                          setEditForm({ ...editForm, Geburtsdatum: undefined });
                                          return;
                                        }
                                        // Try to parse on blur
                                        const match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
                                        if (match) {
                                          const [, day, month, year] = match;
                                          const dayNum = parseInt(day, 10);
                                          const monthNum = parseInt(month, 10);
                                          const yearNum = parseInt(year, 10);
                                          
                                          if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12 && yearNum >= 1900 && yearNum <= 2100) {
                                            const date = new Date(yearNum, monthNum - 1, dayNum);
                                            if (date.getDate() === dayNum && date.getMonth() === monthNum - 1 && date.getFullYear() === yearNum) {
                                              const isoDate = `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                                              setEditForm({ ...editForm, Geburtsdatum: isoDate });
                                              return;
                                            }
                                          }
                                        }
                                        // Invalid format, clear or show error
                                        if (value && !value.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
                                          toast.error('Ungültiges Datumsformat. Bitte verwenden Sie DD.MM.YYYY (z.B. 15.03.2005)');
                                          setEditForm({ ...editForm, Geburtsdatum: undefined });
                                        }
                                      }}
                                      disabled={!canEditCurrentStudent}
                                      className={`input mt-1 w-full pr-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm py-1 ${!canEditCurrentStudent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                      placeholder="DD.MM.YYYY (z.B. 15.03.2005)"
                                      readOnly={!canEditCurrentStudent}
                                    />
                                    <input
                                      type="date"
                                      value={editForm.Geburtsdatum && editForm.Geburtsdatum.match(/^\d{4}-\d{2}-\d{2}$/) 
                                        ? editForm.Geburtsdatum 
                                        : (editForm.Geburtsdatum && editForm.Geburtsdatum.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)
                                          ? (() => {
                                              const match = editForm.Geburtsdatum.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
                                              if (match) {
                                                const [, day, month, year] = match;
                                                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                                              }
                                              return '';
                                            })()
                                          : '')}
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          setEditForm({ ...editForm, Geburtsdatum: e.target.value });
                                        } else {
                                          setEditForm({ ...editForm, Geburtsdatum: undefined });
                                        }
                                      }}
                                      disabled={!canEditCurrentStudent}
                                      className="absolute right-1 top-[calc(0.25rem+1px)] w-8 h-8 opacity-0 cursor-pointer"
                                      title="Kalender öffnen"
                                      style={{ pointerEvents: canEditCurrentStudent ? 'auto' : 'none' }}
                                    />
                                    <Calendar 
                                      className={`absolute right-2 top-[calc(0.25rem+0.5rem)] h-4 w-4 text-gray-400 pointer-events-none ${!canEditCurrentStudent ? 'opacity-50' : ''}`}
                                    />
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Format: DD.MM.YYYY oder Kalender-Icon klicken</p>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Karten ID übernehmen</label>
                                  <button
                                    onClick={handleAdoptCardId}
                                    disabled={adoptCardIdMutation.isLoading || !canEditCurrentStudent}
                                    className={`btn btn-primary mt-1 w-full text-sm py-1 ${!canEditCurrentStudent ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  >
                                    {adoptCardIdMutation.isLoading ? (
                                      <>
                                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                        Prüfe...
                                      </>
                                    ) : (
                                      'Karten ID übernehmen'
                                    )}
                                  </button>
                                </div>
                                <div className="col-span-2">
                                  <label className={`mt-1 flex items-start gap-2 rounded border p-2 ${
                                    Number(editForm.LRS) === 1
                                      ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30'
                                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                                  } ${canEditCurrentStudent ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
                                    <input
                                      type="checkbox"
                                      checked={Number(editForm.LRS) === 1}
                                      onChange={(e) => setEditForm({ ...editForm, LRS: e.target.checked ? 1 : 0 })}
                                      disabled={!canEditCurrentStudent}
                                      className="mt-0.5 text-amber-600 focus:ring-amber-500 rounded"
                                    />
                                    <span className="text-xs text-gray-700 dark:text-gray-300">
                                      <span className="font-semibold text-gray-800 dark:text-gray-100">LRS (Lese-Rechtschreib-Schwäche)</span>
                                      <br />
                                      <span className="text-gray-600 dark:text-gray-400">
                                        Wenn aktiv: Bei Klassenarbeitskorrekturen werden keine Punkte für Rechtschreibfehler abgezogen. Grammatik, Inhalt und andere Kriterien bleiben unverändert.
                                      </span>
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>

                                                         {/* Fächer-Einstufungen - Compact Section */}
                             <div className="rounded-lg border border-green-100 bg-gradient-to-r from-green-50 to-emerald-50 p-4 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                               <h4 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                                 <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                 Fächer-Einstufungen
                               </h4>
                               <div className="grid grid-cols-3 gap-3">
                                 <div className="rounded-lg border border-green-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                                   <label className="mb-2 block text-center text-xs font-medium text-gray-700 dark:text-gray-300">Deutsch</label>
                                   <div className="flex flex-wrap gap-1 justify-center">
                                     {['EK', 'FK', 'GK', 'WK'].map((value) => (
                                       <label key={value} className={`flex items-center space-x-1 rounded bg-gray-50 px-2 py-1 text-xs transition-colors dark:bg-gray-800 ${
                                         canEditCurrentStudent ? 'cursor-pointer hover:bg-green-100 dark:hover:bg-gray-700' : 'cursor-not-allowed opacity-60'
                                       }`}>
                                         <input
                                           type="radio"
                                           name="Diff_D"
                                           value={value}
                                           checked={editForm.Diff_D === value}
                                           onChange={(e) => setEditForm({ ...editForm, Diff_D: e.target.value })}
                                           disabled={!canEditCurrentStudent}
                                           className="text-green-600 focus:ring-green-500"
                                         />
                                         <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{value}</span>
                                       </label>
                                     ))}
                                   </div>
                                 </div>
                                 <div className="rounded-lg border border-green-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                                   <label className="mb-2 block text-center text-xs font-medium text-gray-700 dark:text-gray-300">Mathe</label>
                                   <div className="flex flex-wrap gap-1 justify-center">
                                     {['EK', 'FK', 'GK', 'WK'].map((value) => (
                                       <label key={value} className={`flex items-center space-x-1 rounded bg-gray-50 px-2 py-1 text-xs transition-colors dark:bg-gray-800 ${
                                         canEditCurrentStudent ? 'cursor-pointer hover:bg-green-100 dark:hover:bg-gray-700' : 'cursor-not-allowed opacity-60'
                                       }`}>
                                         <input
                                           type="radio"
                                           name="Diff_M"
                                           value={value}
                                           checked={editForm.Diff_M === value}
                                           onChange={(e) => setEditForm({ ...editForm, Diff_M: e.target.value })}
                                           disabled={!canEditCurrentStudent}
                                           className="text-green-600 focus:ring-green-500"
                                         />
                                         <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{value}</span>
                                       </label>
                                     ))}
                                   </div>
                                 </div>
                                 <div className="rounded-lg border border-green-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                                   <label className="mb-2 block text-center text-xs font-medium text-gray-700 dark:text-gray-300">Englisch</label>
                                   <div className="flex flex-wrap gap-1 justify-center">
                                     {['EK', 'FK', 'GK', 'WK'].map((value) => (
                                       <label key={value} className={`flex items-center space-x-1 rounded bg-gray-50 px-2 py-1 text-xs transition-colors dark:bg-gray-800 ${
                                         canEditCurrentStudent ? 'cursor-pointer hover:bg-green-100 dark:hover:bg-gray-700' : 'cursor-not-allowed opacity-60'
                                       }`}>
                                         <input
                                           type="radio"
                                           name="Diff_E"
                                           value={value}
                                           checked={editForm.Diff_E === value}
                                           onChange={(e) => setEditForm({ ...editForm, Diff_E: e.target.value })}
                                           disabled={!canEditCurrentStudent}
                                           className="text-green-600 focus:ring-green-500"
                                         />
                                         <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{value}</span>
                                       </label>
                                     ))}
                                   </div>
                                 </div>
                               </div>
                             </div>

                             {/* Schülerfoto Section */}
                            <div className="overflow-hidden rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                               <div className="p-3 border-b border-blue-200 bg-gradient-to-r from-blue-500 to-indigo-500">
                                 <h3 className="text-base font-semibold text-white flex items-center">
                                   <CameraOff className="h-4 w-4 mr-2" />
                                   Schülerfoto
                                 </h3>
                               </div>
                               <div className="p-3">
                                 <WebcamCapture
                                   studentId={selectedStudent?.sophomorixUnid || 0}
                                   currentPhoto={hasPhoto(selectedStudentPhoto) ? createPhotoDataUrl(selectedStudentPhoto!) : undefined}
                                   onPhotoUpdated={() => {
                                     // Foto neu laden
                                     if (selectedStudent?.sophomorixUnid) {
                                       loadStudentPhotos([selectedStudent.sophomorixUnid]).then(photos => {
                                         const photo = photos[selectedStudent.sophomorixUnid];
                                         if (photo !== undefined) {
                                           // Update cache directly without using updatePhotoCache function
                                           setStudentPhotos(prev => {
                                             const newPhotos = { ...prev, [selectedStudent.sophomorixUnid]: photo };
                                             
                                             // Update cache order
                                             setPhotoCacheOrder(prevOrder => {
                                               const newOrder = prevOrder.filter(id => id !== selectedStudent.sophomorixUnid);
                                               newOrder.push(selectedStudent.sophomorixUnid);
                                               
                                               // Remove oldest entries if cache is too large
                                               if (newOrder.length > MAX_CACHE_SIZE) {
                                                 const toRemove = newOrder.splice(0, newOrder.length - MAX_CACHE_SIZE);
                                                 toRemove.forEach(id => delete newPhotos[id]);
                                               }
                                               
                                               return newOrder;
                                             });
                                             
                                             return newPhotos;
                                           });
                                         }
                                       });
                                     }
                                   }}
                                   studentData={(() => {
                                     // Only include fields that have actually changed
                                     const changedFields: any = {};
                                     if (editForm && selectedStudent) {
                                       Object.keys(editForm).forEach(key => {
                                         const fieldKey = key as keyof Student;
                                         if (editForm[fieldKey] !== selectedStudent[fieldKey]) {
                                           changedFields[fieldKey] = editForm[fieldKey] as any;
                                         }
                                       });
                                     }
                                     return Object.keys(changedFields).length > 0 ? changedFields : undefined;
                                   })()}
                                   onStudentDataUpdated={() => {
                                     queryClient.invalidateQueries('students')
                                     queryClient.invalidateQueries(['students', sortField, sortDirection, filter])
                                     queryClient.invalidateQueries(['student-photo', selectedStudent?.sophomorixUnid])
                                   }}
                                 />
                               </div>
                             </div>
                            </>
                            )}

                            {/* Erziehungs- und Notfallkontakte */}
                             {selectedStudent && activeSubtab === 'kontakte' && (
                               <div className="mt-4 space-y-4">
                                <StudentGuardianContactsManager
                                   studentUnid={selectedStudent.sophomorixUnid}
                                   studentName={`${selectedStudent.Vorname} ${selectedStudent.Name}`}
                                 />
                                 
                                 {/* Schüler-E-Mail-Verwaltung */}
                                 <StudentEmailsManager 
                                   studentUnid={selectedStudent.sophomorixUnid}
                                   studentName={`${selectedStudent.Vorname} ${selectedStudent.Name}`}
                                 />
                                 
                                 {/* Schülerakte */}
                                 <StudentFilesManager 
                                   studentUnid={selectedStudent.sophomorixUnid}
                                   studentName={`${selectedStudent.Vorname} ${selectedStudent.Name}`}
                                 />
                               </div>
                             )}
                          </div>
                        )}

                                                 {activeTab === 'performance' && (
                           <div className="space-y-4">
                             {/* Fächer-Einstufungen Anzeige */}
                            <div className="rounded-lg border border-green-100 bg-gradient-to-r from-green-50 to-emerald-50 p-4 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                              <h4 className="mb-3 flex items-center text-base font-semibold text-gray-800 dark:text-gray-100">
                                 <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                 Aktuelle Fächer-Einstufungen
                               </h4>
                               <div className="grid grid-cols-3 gap-4">
                                <div className="rounded-lg border border-green-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                                  <label className="mb-2 block text-center text-xs font-medium text-gray-700 dark:text-gray-300">Deutsch</label>
                                   <div className="text-center">
                                     <span className="text-lg font-bold text-green-600">{selectedStudent.Diff_D || '-'}</span>
                                   </div>
                                 </div>
                                <div className="rounded-lg border border-green-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                                  <label className="mb-2 block text-center text-xs font-medium text-gray-700 dark:text-gray-300">Mathematik</label>
                                   <div className="text-center">
                                     <span className="text-lg font-bold text-green-600">{selectedStudent.Diff_M || '-'}</span>
                                   </div>
                                 </div>
                                <div className="rounded-lg border border-green-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                                  <label className="mb-2 block text-center text-xs font-medium text-gray-700 dark:text-gray-300">Englisch</label>
                                   <div className="text-center">
                                     <span className="text-lg font-bold text-green-600">{selectedStudent.Diff_E || '-'}</span>
                                   </div>
                                 </div>
                               </div>
                             </div>

                                                           {/* Assessment Section */}
                             <div className="overflow-hidden rounded-lg border border-green-100 bg-gradient-to-br from-green-50 to-emerald-50 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                                <div className="p-3 border-b border-green-200 bg-gradient-to-r from-green-500 to-emerald-500">
                                  <h3 className="text-base font-semibold text-white flex items-center">
                                    <AlertTriangle className="h-4 w-4 mr-2" />
                                    Einschätzung der Lernrakete
                                  </h3>
                                </div>
                                <div className="p-3">
                                  {kiAssessment ? (
                                    <div className={`p-3 rounded-lg border ${getKIAssessmentColor(kiAssessment)}`}>
                                      <div className="flex items-start space-x-2">
                                        {getKIAssessmentLevel(kiAssessment) === 'red' ? (
                                          <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        ) : getKIAssessmentLevel(kiAssessment) === 'yellow' ? (
                                          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                        ) : getKIAssessmentLevel(kiAssessment) === 'green' ? (
                                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                        ) : (
                                          <div className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                        )}
                                        <div className="flex-1">
                                          <Badge variant={getKIAssessmentBadgeVariant(kiAssessment)} className="mb-1">
                                            {getKIAssessmentLabel(kiAssessment)}
                                          </Badge>
                                          <p className="text-xs font-medium leading-relaxed">{kiAssessment.memo}</p>
                                          <p className="mt-1 inline-block rounded-full bg-white px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                            {kiAssessment.datum}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-center py-4">
                                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <AlertTriangle className="h-6 w-6 text-gray-400" />
                                      </div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">Keine KI-Einschätzung verfügbar</p>
                                    </div>
                                  )}
                                </div>
                              </div>

                                                           {/* Memo Section - Compact */}
                              <div className="overflow-hidden rounded-lg border border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                                <div className="p-3 border-b border-purple-200 bg-gradient-to-r from-purple-500 to-pink-500">
                                  <h3 className="text-base font-semibold text-white flex items-center">
                                    <Edit className="h-4 w-4 mr-2" />
                                    Notizen aus dem Lernbüro
                                  </h3>
                                </div>
                                                                                                  <div className="p-3">
                                    {isEditingMemo ? (
                                      <div className="space-y-3">
                                        <textarea
                                          value={memoText}
                                          onChange={(e) => setMemoText(e.target.value)}
                                          disabled={!canEditCurrentStudent}
                                          className={`w-full resize-none rounded-lg border border-purple-200 p-3 text-xs leading-relaxed text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 ${!canEditCurrentStudent ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-800' : ''}`}
                                          rows={4}
                                          placeholder="Notizen eingeben..."
                                          readOnly={!canEditCurrentStudent}
                                        />
                                        <div className="flex space-x-2">
                                          <button
                                            onClick={handleSaveMemo}
                                            className={`btn btn-primary bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors ${!canEditCurrentStudent ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            disabled={updateStudentMemoMutation.isLoading || !canEditCurrentStudent}
                                          >
                                            {updateStudentMemoMutation.isLoading ? (
                                              <>
                                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                                Speichere...
                                              </>
                                            ) : (
                                              <>
                                                <Save className="h-3 w-3 mr-1" />
                                                Speichern
                                              </>
                                            )}
                                          </button>
                                          <button
                                            onClick={handleCancelMemoEdit}
                                            className="btn btn-outline border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                                          >
                                            Abbrechen
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        {selectedStudent?.Memo ? (
                                          <div className="rounded-lg border border-purple-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900">
                                            <p className="text-xs leading-relaxed text-gray-900 dark:text-gray-100">{selectedStudent.Memo}</p>
                                          </div>
                                        ) : (
                                          <div className="text-center py-4">
                                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                              <Edit className="h-6 w-6 text-gray-400" />
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Keine Notizen verfügbar</p>
                                          </div>
                                        )}
                                        <button
                                          onClick={() => setIsEditingMemo(true)}
                                          disabled={!canEditCurrentStudent}
                                          className={`btn btn-outline border-purple-300 px-3 py-1 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-50 dark:border-purple-700 dark:text-purple-300 dark:hover:bg-purple-900/30 w-full ${!canEditCurrentStudent ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                          <Edit className="h-3 w-3 mr-1" />
                                          Notizen bearbeiten
                                        </button>
                                      </div>
                                    )}
                                  </div>
                              </div>

                                                             {/* Performance Data Table Section */}
                               <div className="overflow-hidden rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                                 <div className="p-3 border-b border-blue-200 bg-gradient-to-r from-blue-500 to-indigo-500">
                                   <h3 className="text-base font-semibold text-white flex items-center">
                                     <CheckCircle className="h-4 w-4 mr-2" />
                                     Leistungsdaten (Belege)
                                   </h3>
                                 </div>
                                 
                                 {/* Filter Section */}
                                 <div className="border-b border-blue-100 bg-blue-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                   <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                     <div>
                                      <label className="mb-1 block text-xs font-medium text-blue-900 dark:text-gray-300">Freitext-Suche</label>
                                       <input
                                         type="text"
                                         value={performanceSearch}
                                         onChange={(e) => handlePerformanceSearchChange(e.target.value)}
                                         placeholder="Fach, Baustein, Lernplan, Aufgabe, Lehrer..."
                                         className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                       />
                                     </div>
                                     <div>
                                      <label className="mb-1 block text-xs font-medium text-blue-900 dark:text-gray-300">Von Datum</label>
                                       <input
                                         type="date"
                                         value={performanceStartDate}
                                         onChange={(e) => handlePerformanceStartDateChange(e.target.value)}
                                         className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                       />
                                     </div>
                                     <div>
                                      <label className="mb-1 block text-xs font-medium text-blue-900 dark:text-gray-300">Bis Datum</label>
                                       <input
                                         type="date"
                                         value={performanceEndDate}
                                         onChange={(e) => handlePerformanceEndDateChange(e.target.value)}
                                         className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                       />
                                     </div>
                                     <div className="flex items-end">
                                       <button
                                         onClick={clearPerformanceFilters}
                                         className="w-full px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                       >
                                         Filter löschen
                                       </button>
                                     </div>
                                   </div>
                                 </div>
                                 
                                                                  <div className="p-3">
                                   {/* Results count */}
                                  <div className="mb-3 text-xs text-blue-700 dark:text-blue-300">
                                     {performanceData ? (
                                       <span>
                                         {performanceData.length} Eintrag{performanceData.length !== 1 ? 'e' : ''} gefunden
                                         {(performanceSearch || performanceStartDate || performanceEndDate) && ' (gefiltert)'}
                                       </span>
                                     ) : (
                                       <span>Lade Daten...</span>
                                     )}
                                   </div>
                                   
                                   {performanceData && performanceData.length > 0 ? (
                                     <TableScrollArea>
                                       <Table>
                                         <TableHead>
                                           <TableRow>
                                             <SortableTh
                                               sortKey="Fach"
                                               activeKey={performanceSortField}
                                               direction={performanceSortDirection}
                                               onSort={handlePerformanceSort}
                                               className="w-16"
                                             >
                                               Fach
                                             </SortableTh>
                                             <SortableTh
                                               sortKey="Baustein"
                                               activeKey={performanceSortField}
                                               direction={performanceSortDirection}
                                               onSort={handlePerformanceSort}
                                             >
                                               Baustein
                                             </SortableTh>
                                             <SortableTh
                                               sortKey="Lernplan"
                                               activeKey={performanceSortField}
                                               direction={performanceSortDirection}
                                               onSort={handlePerformanceSort}
                                             >
                                               Lernplan
                                             </SortableTh>
                                             <SortableTh
                                               sortKey="Aufgabe"
                                               activeKey={performanceSortField}
                                               direction={performanceSortDirection}
                                               onSort={handlePerformanceSort}
                                               className="w-20"
                                             >
                                               Aufgabe
                                             </SortableTh>
                                             <SortableTh
                                               sortKey="Lehrer"
                                               activeKey={performanceSortField}
                                               direction={performanceSortDirection}
                                               onSort={handlePerformanceSort}
                                             >
                                               Lehrer
                                             </SortableTh>
                                             <SortableTh
                                               sortKey="Datum"
                                               activeKey={performanceSortField}
                                               direction={performanceSortDirection}
                                               onSort={handlePerformanceSort}
                                             >
                                               Datum
                                             </SortableTh>
                                           </TableRow>
                                         </TableHead>
                                         <TableBody>
                                           {performanceData.map((record, index) => (
                                             <TableRow key={index}>
                                               <TableCell className="w-16 text-xs">{record.Fach}</TableCell>
                                               <TableCell className="text-xs">{record.Baustein}</TableCell>
                                               <TableCell className="text-xs">{record.Lernplan}</TableCell>
                                               <TableCell className="w-20 text-xs">{record.Aufgabe}</TableCell>
                                               <TableCell className="text-xs">{record.Lehrer}</TableCell>
                                               <TableCell className="text-xs">{record.Datum}</TableCell>
                                             </TableRow>
                                           ))}
                                         </TableBody>
                                       </Table>
                                     </TableScrollArea>
                                  ) : (
                                    <div className="text-center py-4">
                                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <CheckCircle className="h-6 w-6 text-gray-400" />
                                      </div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">Keine Leistungsdaten verfügbar</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                           </div>
                         )}

                        {activeTab === 'kompetenzen' && selectedStudent && (
                          <StudentKompetenzenPanel sophomorixUnid={selectedStudent.sophomorixUnid} />
                        )}
                      </div>
                    </Tile>

                      {activeTab === 'attendance' && (
                        <div className="space-y-4" data-tab="attendance">
                          <AttendanceCalendar studentId={selectedStudent.sophomorixUnid} />
                        </div>
                      )}

                      {activeTab === 'shortAbsences' && (
                        <div className="space-y-4" data-tab="shortAbsences">
                          {shortAbsenceSummary && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="rounded-lg border border-amber-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Durchschnittliche Abwesenheit</div>
                                <div className="text-lg font-semibold text-amber-700">
                                  {formatShortAbsenceDuration(shortAbsenceSummary.averageDurationSeconds)}
                                </div>
                              </div>
                              <div className="rounded-lg border border-amber-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Durchschnitt pro Woche</div>
                                <div className="text-lg font-semibold text-amber-700">
                                  {shortAbsenceSummary.averagePerWeek}x/Woche
                                </div>
                                <div className="text-xs text-gray-400 dark:text-gray-500">Basis: {shortAbsenceSummary.weekCount} Woche(n)</div>
                              </div>
                            </div>
                          )}
                          <div className="overflow-hidden rounded-lg border border-amber-100 bg-gradient-to-br from-amber-50 to-yellow-50 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                            <div className="p-3 border-b border-amber-200 bg-gradient-to-r from-amber-500 to-yellow-500">
                              <h3 className="text-base font-semibold text-white flex items-center">
                                <Clock className="h-4 w-4 mr-2" />
                                Kurzzeitige Abwesenheiten
                              </h3>
                            </div>
                            <div className="p-3">
                              {isLoadingShortAbsences ? (
                                <div className="flex justify-center items-center py-12">
                                  <RefreshCw className="w-8 h-8 animate-spin text-amber-600" />
                                  <span className="ml-3 text-gray-600 dark:text-gray-300">Lade Abwesenheiten...</span>
                                </div>
                              ) : shortAbsenceRecords.length > 0 ? (
                                <TableScrollArea>
                                  <Table>
                                    <TableHead>
                                      <TableRow>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Datum</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Start</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Ende</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Dauer</th>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {shortAbsenceRecords.map((record) => (
                                        <TableRow key={record.id}>
                                          <TableCell className="text-xs">{record.datum || '-'}</TableCell>
                                          <TableCell className="text-xs">{record.startUhrzeit || '-'}</TableCell>
                                          <TableCell className="text-xs">{record.endUhrzeit || 'aktiv'}</TableCell>
                                          <TableCell className="text-xs font-medium text-amber-700 dark:text-amber-300">{formatShortAbsenceDuration(record.dauerSekunden)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableScrollArea>
                              ) : (
                                <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
                                  Keine kurzzeitigen Abwesenheiten vorhanden
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === 'leistungsnachweise' && (
                        <div className="space-y-4" data-tab="leistungsnachweise">
                          {/* Filter Section */}
                          <div className="overflow-hidden rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                            <div className="p-3 border-b border-blue-200 bg-gradient-to-r from-blue-500 to-indigo-500">
                              <h3 className="text-base font-semibold text-white flex items-center">
                                <Filter className="h-4 w-4 mr-2" />
                                Lernpläne Filter
                              </h3>
                            </div>
                            <div className="border-b border-blue-100 bg-blue-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                              <div className="flex items-center gap-3">
                                <div className="flex-1">
                                  <label className="mb-1 block text-xs font-medium text-blue-900 dark:text-gray-300">Freitext-Suche</label>
                                  <input
                                    type="text"
                                    value={learningPlansSearch}
                                    onChange={(e) => setLearningPlansSearch(e.target.value)}
                                    placeholder="Nach Name, Fach, Baustein suchen..."
                                    className="input-field w-full py-1 text-xs"
                                  />
                                </div>
                                <div className="flex items-end gap-2">
                                  <button
                                    onClick={() => {
                                      setLearningPlansSearch('')
                                    }}
                                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                  >
                                    Filter löschen
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Learning Plans Table */}
                          <div className="overflow-hidden rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                            <div className="p-3 border-b border-blue-200 bg-gradient-to-r from-blue-500 to-indigo-500">
                              <h3 className="text-base font-semibold text-white flex items-center">
                                <FileText className="h-4 w-4 mr-2" />
                                Lernpläne
                              </h3>
                            </div>
                            
                            <div className="p-3">
                              {isLoadingLearningPlans ? (
                                <div className="flex justify-center items-center py-12">
                                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                                  <span className="ml-3 text-gray-600 dark:text-gray-300">Lade Lernpläne...</span>
                                </div>
                              ) : learningPlansData && learningPlansData.length > 0 ? (
                                <>
                                  {/* Results count */}
                                  <div className="mb-3 text-xs text-blue-700 dark:text-blue-300">
                                    {(() => {
                                      const filtered = learningPlansData.filter((plan: any) => {
                                        if (!learningPlansSearch) return true
                                        const search = learningPlansSearch.toLowerCase()
                                        return (
                                          plan.NAME?.toLowerCase().includes(search) ||
                                          plan.FACH?.toLowerCase().includes(search) ||
                                          plan.Baustein_Name?.toLowerCase().includes(search)
                                        )
                                      })
                                      return filtered
                                    })().length} Lernplan{(() => {
                                      const filtered = learningPlansData.filter((plan: any) => {
                                        if (!learningPlansSearch) return true
                                        const search = learningPlansSearch.toLowerCase()
                                        return (
                                          plan.NAME?.toLowerCase().includes(search) ||
                                          plan.FACH?.toLowerCase().includes(search) ||
                                          plan.Baustein_Name?.toLowerCase().includes(search)
                                        )
                                      })
                                      return filtered
                                    })().length !== 1 ? 'e' : ''} gefunden
                                    {learningPlansSearch && ' (gefiltert)'}
                                  </div>
                                  
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-blue-200 dark:divide-gray-700">
                                      <thead className="bg-blue-50 dark:bg-gray-800/80">
                                        <tr>
                                          <th
                                            className="cursor-pointer px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900 hover:bg-blue-100 dark:text-gray-200 dark:hover:bg-gray-700/70"
                                            onClick={() => {
                                              if (learningPlansSortField === 'NAME') {
                                                setLearningPlansSortDirection(learningPlansSortDirection === 'asc' ? 'desc' : 'asc')
                                              } else {
                                                setLearningPlansSortField('NAME')
                                                setLearningPlansSortDirection('asc')
                                              }
                                            }}
                                          >
                                            <div className="flex items-center space-x-1">
                                              <span>Name</span>
                                              {learningPlansSortField === 'NAME' && (
                                                <span className="text-blue-600">
                                                  {learningPlansSortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                              )}
                                            </div>
                                          </th>
                                          <th
                                            className="cursor-pointer px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900 hover:bg-blue-100 dark:text-gray-200 dark:hover:bg-gray-700/70"
                                            onClick={() => {
                                              if (learningPlansSortField === 'FACH') {
                                                setLearningPlansSortDirection(learningPlansSortDirection === 'asc' ? 'desc' : 'asc')
                                              } else {
                                                setLearningPlansSortField('FACH')
                                                setLearningPlansSortDirection('asc')
                                              }
                                            }}
                                          >
                                            <div className="flex items-center space-x-1">
                                              <span>Fach</span>
                                              {learningPlansSortField === 'FACH' && (
                                                <span className="text-blue-600">
                                                  {learningPlansSortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                              )}
                                            </div>
                                          </th>
                                          <th
                                            className="cursor-pointer px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900 hover:bg-blue-100 dark:text-gray-200 dark:hover:bg-gray-700/70"
                                            onClick={() => {
                                              if (learningPlansSortField === 'BAUSTEIN') {
                                                setLearningPlansSortDirection(learningPlansSortDirection === 'asc' ? 'desc' : 'asc')
                                              } else {
                                                setLearningPlansSortField('BAUSTEIN')
                                                setLearningPlansSortDirection('asc')
                                              }
                                            }}
                                          >
                                            <div className="flex items-center space-x-1">
                                              <span>Baustein</span>
                                              {learningPlansSortField === 'BAUSTEIN' && (
                                                <span className="text-blue-600">
                                                  {learningPlansSortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                              )}
                                            </div>
                                          </th>
                                          <th
                                            className="cursor-pointer px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900 hover:bg-blue-100 dark:text-gray-200 dark:hover:bg-gray-700/70"
                                            onClick={() => {
                                              if (learningPlansSortField === 'START_DATUM') {
                                                setLearningPlansSortDirection(learningPlansSortDirection === 'asc' ? 'desc' : 'asc')
                                              } else {
                                                setLearningPlansSortField('START_DATUM')
                                                setLearningPlansSortDirection('desc')
                                              }
                                            }}
                                          >
                                            <div className="flex items-center space-x-1">
                                              <span>Start</span>
                                              {learningPlansSortField === 'START_DATUM' && (
                                                <span className="text-blue-600">
                                                  {learningPlansSortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                              )}
                                            </div>
                                          </th>
                                          <th
                                            className="cursor-pointer px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-blue-900 hover:bg-blue-100 dark:text-gray-200 dark:hover:bg-gray-700/70"
                                            onClick={() => {
                                              if (learningPlansSortField === 'END_DATUM') {
                                                setLearningPlansSortDirection(learningPlansSortDirection === 'asc' ? 'desc' : 'asc')
                                              } else {
                                                setLearningPlansSortField('END_DATUM')
                                                setLearningPlansSortDirection('desc')
                                              }
                                            }}
                                          >
                                            <div className="flex items-center space-x-1">
                                              <span>Ende</span>
                                              {learningPlansSortField === 'END_DATUM' && (
                                                <span className="text-blue-600">
                                                  {learningPlansSortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                              )}
                                            </div>
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-blue-100 bg-white dark:divide-gray-700 dark:bg-gray-900">
                                        {(() => {
                                          // Filter and sort
                                          let filtered = learningPlansData.filter((plan: any) => {
                                            if (!learningPlansSearch) return true
                                            const search = learningPlansSearch.toLowerCase()
                                            return (
                                              plan.NAME?.toLowerCase().includes(search) ||
                                              plan.FACH?.toLowerCase().includes(search) ||
                                              plan.Baustein_Name?.toLowerCase().includes(search)
                                            )
                                          })
                                          
                                          // Sort
                                          filtered = [...filtered].sort((a: any, b: any) => {
                                            let aVal: any
                                            let bVal: any
                                            
                                            // Map sort field to actual object field name
                                            if (learningPlansSortField === 'BAUSTEIN') {
                                              aVal = a.Baustein_Name
                                              bVal = b.Baustein_Name
                                            } else {
                                              aVal = a[learningPlansSortField]
                                              bVal = b[learningPlansSortField]
                                            }
                                            
                                            // Handle dates
                                            if (learningPlansSortField === 'START_DATUM' || learningPlansSortField === 'END_DATUM') {
                                              aVal = aVal ? new Date(aVal).getTime() : 0
                                              bVal = bVal ? new Date(bVal).getTime() : 0
                                            } else {
                                              aVal = aVal?.toString().toLowerCase() || ''
                                              bVal = bVal?.toString().toLowerCase() || ''
                                            }
                                            
                                            if (learningPlansSortDirection === 'asc') {
                                              return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
                                            } else {
                                              return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
                                            }
                                          })
                                          
                                          return filtered
                                        })().map((plan: any) => {
                                          const startDate = plan.START_DATUM ? new Date(plan.START_DATUM).toLocaleDateString('de-DE') : '-'
                                          const endDate = plan.END_DATUM ? new Date(plan.END_DATUM).toLocaleDateString('de-DE') : '-'
                                          const isActive = plan.is_active === 1
                                          
                                          return (
                                            <tr
                                              key={plan.LPID}
                                              className="cursor-pointer transition-colors hover:bg-blue-50 dark:hover:bg-gray-800/60"
                                              onClick={() => {
                                                setSelectedLearningPlan(plan)
                                                setShowLearningPlanModal(true)
                                              }}
                                            >
                                              <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-gray-100">{plan.NAME || '-'}</td>
                                              <td className="px-3 py-2 text-xs text-gray-900 dark:text-gray-100">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                  plan.FACH === 'Deutsch' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200' :
                                                  plan.FACH === 'Mathematik' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200' :
                                                  plan.FACH === 'Englisch' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200' :
                                                  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                                                }`}>
                                                  {plan.FACH || '-'}
                                                </span>
                                              </td>
                                              <td className="px-3 py-2 text-xs text-gray-900 dark:text-gray-100">{plan.Baustein_Name || '-'}</td>
                                              <td className="px-3 py-2 text-xs text-gray-900 dark:text-gray-100">{startDate}</td>
                                              <td className="px-3 py-2 text-xs text-gray-900 dark:text-gray-100">{endDate}</td>
                                            </tr>
                                          )
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              ) : (
                                <div className="text-center py-4">
                                  <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                                    <FileText className="h-6 w-6 text-gray-400" />
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">Keine Lernpläne verfügbar</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons - Bottom Section */}
                      <div className="rounded-lg border border-purple-100 bg-gradient-to-r from-purple-50 to-pink-50 p-4 dark:border-gray-700 dark:from-gray-900 dark:to-gray-900">
                        <h4 className="mb-3 flex items-center text-base font-semibold text-gray-800 dark:text-gray-100">
                          <Save className="h-4 w-4 mr-2 text-purple-600" />
                          Aktionen
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={handleSave}
                            size="sm"
                            disabled={updateStudentMutation.isLoading || !canEditCurrentStudent}
                          >
                            {updateStudentMutation.isLoading ? (
                              <>
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                Speichere...
                              </>
                            ) : (
                              <>
                                <Save className="h-3 w-3 mr-1" />
                                Speichern
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => transferToLearningOfficeMutation.mutate(selectedStudent.ID)}
                            size="sm"
                            disabled={transferToLearningOfficeMutation.isLoading || !canEditCurrentStudent}
                          >
                            Schüler ins Lernbüro übernehmen
                          </Button>

                          <Button
                            onClick={() => setShowExposeModal(true)}
                            variant="outline"
                            size="sm"
                          >
                            <FileText className="mr-1 h-3 w-3" />
                            Exposé erstellen
                          </Button>

                        </div>
                      </div>
                </>
              ) : (
                <Tile>
                  <TileContent className="p-8 text-center">
                    <Users className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-gray-500" />
                    <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">Kein Schüler ausgewählt</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Wählen Sie einen Schüler aus der Liste aus, um Details anzuzeigen.</p>
                  </TileContent>
                </Tile>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add New Student Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Neuen Schüler hinzufügen"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCreateStudent}
              disabled={createStudentMutation.isLoading}
            >
              {createStudentMutation.isLoading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Erstelle...
                </>
              ) : (
                'Schüler erstellen'
              )}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Name *</label>
                <input
                  type="text"
                  value={newStudentForm.Name || ''}
                  onChange={(e) => handleNewStudentFormChange('Name', e.target.value)}
                  className="input-field w-full"
                  placeholder="Name eingeben"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Vorname *</label>
                <input
                  type="text"
                  value={newStudentForm.Vorname || ''}
                  onChange={(e) => handleNewStudentFormChange('Vorname', e.target.value)}
                  className="input-field w-full"
                  placeholder="Vorname eingeben"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Klasse *</label>
                <input
                  type="text"
                  value={newStudentForm.Klasse || ''}
                  onChange={(e) => handleNewStudentFormChange('Klasse', e.target.value)}
                  className="input-field w-full"
                  placeholder="Klasse eingeben"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                <input
                  type="email"
                  value={newStudentForm.email || ''}
                  onChange={(e) => handleNewStudentFormChange('email', e.target.value)}
                  className="input-field w-full"
                  placeholder="Email eingeben"
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">RFID/Chipkarten ID</label>
                <input
                  type="text"
                  value={newStudentForm.RFID || ''}
                  onChange={(e) => handleNewStudentFormChange('RFID', e.target.value)}
                  className="input-field w-full"
                  placeholder="RFID eingeben"
                />
              </div>
              

              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Deutsch</label>
                  <select
                    value={newStudentForm.Diff_D || 'GK'}
                    onChange={(e) => handleNewStudentFormChange('Diff_D', e.target.value)}
                    className="input-field w-full"
                  >
                    <option value="EK">EK</option>
                    <option value="FK">FK</option>
                    <option value="GK">GK</option>
                    <option value="WK">WK</option>
                  </select>
                </div>
                
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Mathe</label>
                  <select
                    value={newStudentForm.Diff_M || 'GK'}
                    onChange={(e) => handleNewStudentFormChange('Diff_M', e.target.value)}
                    className="input-field w-full"
                  >
                    <option value="EK">EK</option>
                    <option value="FK">FK</option>
                    <option value="GK">GK</option>
                    <option value="WK">WK</option>
                  </select>
                </div>
                
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Englisch</label>
                  <select
                    value={newStudentForm.Diff_E || 'GK'}
                    onChange={(e) => handleNewStudentFormChange('Diff_E', e.target.value)}
                    className="input-field w-full"
                  >
                    <option value="EK">EK</option>
                    <option value="FK">FK</option>
                    <option value="GK">GK</option>
                    <option value="WK">WK</option>
                  </select>
                </div>
              </div>
        </div>
      </Modal>

      {/* Student Expose Modal */}
      {showExposeModal && selectedStudent && (
        <StudentExpose
          studentUnid={selectedStudent.sophomorixUnid}
          onClose={() => setShowExposeModal(false)}
        />
      )}

      {/* Learning Plan Tasks Modal */}
      {showLearningPlanModal && selectedStudent && selectedLearningPlan && (
        <LearningPlanTasksModal
          isOpen={showLearningPlanModal}
          onClose={() => {
            setShowLearningPlanModal(false)
            setSelectedLearningPlan(null)
          }}
          studentUnid={selectedStudent.sophomorixUnid}
          learningPlan={selectedLearningPlan}
        />
      )}
    </div>
  )
}
